/**
 * Search Controller. Responsible for initializing the search component
 * and syndicating search requests and results and synchronizing the UI layer.
 */
import _ from 'lodash';
import jQuery from 'jquery';
import MessageBusTopics from '../../../common/MessageBusTopics';
import SearchRecentController from './SearchRecentController';
import SearchService from './services/SearchService';
import SearchUiController from './uiComponent/SearchUiController';
import AdvancedSearchUiController from './uiComponent/AdvancedSearchUiController';
import URLProvider from '../URLProvider';
import DeviceDetector from '../DeviceDetector';
import { isUserAgentWithDeviceId } from '../../../utils';
import { iosDeviceIds } from '../../constants/deviceIds';

/**
 * Search Controller constructor
 *
 * @constructor
 */
function SearchController() {

    this.isSearchOverlayOpen = false;
}

/**
 * Check in config if search is enabled
 *
 * @returns {Boolean}
 *
 * @private
 */
SearchController.prototype.isSearchEnabled = function () {

    return !!this.config.enabled;
};

/**
 * Check against device and config if the current device is supported by search
 *
 * @returns {Boolean}
 *
 * @private
 */
SearchController.prototype.isSearchEnabledForPlatform = function () {

    var isMobile = this.deps.DeviceDetector.isMobile(),
        mobilePlatform = !!(this.config && this.config.platforms && this.config.platforms.mobile),
        desktopPlatform = !!(this.config && this.config.platforms && this.config.platforms.desktop);

    return (isMobile && mobilePlatform) || (!isMobile && desktopPlatform);
};

/**
 * Create and render a new Search UI Controller and bind actionable
 * events to handlers
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.initSearchUiController = function () {

    this.searchUiController = new SearchUiController(this.deps);

    if (
        (this.config.features && this.config.features.recentSearches === true) ||
        window.localStorage.getItem('WH.Search.Features.recentSearches') === 'on'
    ) {
        this.recentSearchesController = new SearchRecentController(
            this.config,
            this.searchService
        );
    }

    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_INPUT_CHANGE,
        _.debounce(this.onInputChange.bind(this), this.config.debounceWait)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_EVENTS_LOAD_MORE, this.onLoadMore.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_OPEN, this.onOpenSearch.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_CLOSE, this.onCloseSearch.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_EVENT_RESULT_ITEM_CLICK, this.onEventResultItemClick.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_ICON_BAR_ITEM_CLICK, this.onIconBarItemClick.bind(this)
    );
};

SearchController.prototype.initAdvancedSearchUiController = function () {

    this.advancedSearchUiController = new AdvancedSearchUiController(this.deps);

    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_OPEN, this.onOpenSearch.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_CLOSE, this.onCloseSearch.bind(this)
    );
};

/**
 * Listen to input and perform a new search if the search value is
 * different from the previous search
 *
 * @param  {Object} event The 'search.input.change' event
 * @param  {Object} domEvent The event inputEvent on the DOM element
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.onInputChange = function (event, domEvent) {

    var lengthThreshold = 3,
        value = domEvent.target.value.trim(),
        state = this.searchService.resetState();

    // Don't search if either:
    //  - the search term length shorter than the threshold
    //  - the input matches previous search term.
    if ((lengthThreshold > value.length) || (value === state.searchString)) {

        this.searchService.abortRequest();
        window.WH.messageBus.publish(MessageBusTopics.SEARCH_STATE_RESET);
        return;
    }

    window.WH.messageBus.publish(MessageBusTopics.SEARCH_REQUEST_START);

    this.searchService.search(value, 1)
        .done(this.updateUiComponentFromSearchState.bind(this, false))
        .fail(this.handleSearchError.bind(this));
};

/**
 * Listen for actions to load more results. Loads next page of results based
 * on previous search state
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.onLoadMore = function () {

    var searchState = this.searchService.getState(),
        page = searchState.pagination.currentPage + 1;

    window.WH.messageBus.publish(MessageBusTopics.SEARCH_REQUEST_START_LOAD_MORE);

    this.searchService.search(searchState.searchString, page)
        .done(this.updateUiComponentFromSearchState.bind(this, true))
        .fail(this.handleSearchError.bind(this));
};

/**
 * Listen for closing the search overlay event
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.onOpenSearch = function () {

    this.isSearchOverlayOpen = true;
};

/**
 * Listen for closing the search overlay event
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.onCloseSearch = function () {
    this.clearSearch();
    this.isSearchOverlayOpen = false;
    document.body.style.overflow = 'unset';
};

/**
 * Listen for the event results clicking event
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.onEventResultItemClick = function () {

    this.clearSearch();
};

/**
 * Listen for the sport results clicking event
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.onIconBarItemClick = function () {

    this.clearSearch();
};

/**
 * Fail callback for the search request
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.handleSearchError = function (error) {

    window.WH.messageBus.publish(MessageBusTopics.SEARCH_REQUEST_FAILED, error);
};

/**
 * Update the UI component to reflect the latest search state.
 * This is the success callback for the search request.
 *
 * @param  {Boolean} showOnlyNewResults Flag to render only additional items
 *                                      from search state - it is true once
 *                                      load more has triggered
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.updateUiComponentFromSearchState = function (showOnlyNewResults) {

    showOnlyNewResults = !!showOnlyNewResults;

    var searchState = this.searchService.getState(),
        eventItems = showOnlyNewResults ? searchState.newEvents : searchState.events;

    var hasAnyResult = !!searchState.totalSports || !!searchState.totalEvents || !!searchState.totalCustom;

    var isLoadMore = showOnlyNewResults;

    window.WH.messageBus.publish(MessageBusTopics.SEARCH_REQUEST_SUCCESS, {
        shouldSearchTermBeSaved: (!isLoadMore && hasAnyResult),
    });

    var searchResultItems = {
        sports: {
            total: searchState.totalSports,
            items: searchState.sports,
        },
        events: {
            total: searchState.totalEvents,
            items: eventItems,
        },
        custom: {
            total: searchState.totalCustom,
            items: searchState.custom,
        },
    };

    this.searchUiController.update(searchResultItems, {
        reset: !showOnlyNewResults,
        hasMoreResults: searchState.pagination.currentPage < searchState.pagination.totalPages,
    });
};

/**
 * Clear and reset the UI interface
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.clearSearch = function () {

    // this.searchService is undefined when advancedSearch is enabled
    if (this.searchService) {
        this.searchService.resetState();
    }

    window.WH.messageBus.publish(MessageBusTopics.SEARCH_STATE_RESET);

    // this.searchUiController is undefined when advancedSearch is enabled
    if (this.searchUiController) {
        this.searchUiController.update({}, {
            reset: true,
            hasMoreResults: false,
        });
    }
};

/**
 * Configure dependencies and instantiate a new UI component interface
 *
 * @returns {void}
 *
 * @private
 */
SearchController.prototype.setup = function () {
    if (this.config.features && this.config.features.advancedSearch) {
        this.initAdvancedSearchUiController();
    } else {
        this.searchService = new SearchService({
            lang: this.lang,
            i18n: this.i18n,
            funnelBackUrl: this.config.url,
        }, this.deps);
        this.initSearchUiController();
    }

    this.setSearchVisibility();
};

/**
 * Setting the search bar visibility
 *      .com - hide for all devices
 *      .es .it - show for mobile iOS native app and iPads
 *
 * @returns {void}
 */
SearchController.prototype.setSearchVisibility = function () {
    const searchSelector = '#search-header';
    const isComJurisdiction = window.whStatic.config.jurisdiction === '.com';
    const isNative = DeviceDetector.isNative();
    const isIOS = DeviceDetector.isIOS();
    const isMobile = DeviceDetector.isMobile();
    const isIosDeviceWithId = isUserAgentWithDeviceId(window.navigator.userAgent, iosDeviceIds);
    const appVersionString = DeviceDetector.getAppVersion();

    /*
    Temporary solution:
    We check ios device Ids which are appended to user agent strings,
    because we can't rely on appVersion cookie in international platform as of today.
    Com & International handle request headers & cookies differently.
    It should be refactored to be called only for isNativeIos, right after
    the trafficscript responsible for setting the wh_device cookie for
    international platform is reliable. Meaning, this issue is done:
    https://jira.willhillatlas.com/browse/MRGIOSAPPS-1192
    */
    const isNativeIOS = (isIOS || isNative) && isIosDeviceWithId;
    let attempts = 0;

    if (isIOS) {
        // display search input even for big iPad
        jQuery(searchSelector).removeClass('desktop-hide');
    }

    if (isIOS && !isComJurisdiction && appVersionString) {
        const [major, minor] = appVersionString.split('.').map(str => Number(str));
        if ((major === 5 && minor >= 2) || major > 5) {
            document.querySelector(searchSelector).remove();
        }
    }

    const searchInterval = setInterval(function () {
        if (isComJurisdiction) {
            jQuery(searchSelector).addClass('wh-hide');
        } else if (isMobile && !isNativeIOS) {
            jQuery(searchSelector).addClass('wh-hide');
        }
        else {
            jQuery(searchSelector).removeClass('wh-hide');
        }

        attempts++;
        if (attempts >= 9) {
            clearInterval(searchInterval);
        }
    }, 300);
};

/**
 * Closing the search overlay when navigating away
 *      eg: pressing a back button or a navigation message published
 *
 * @returns {void}
 */
SearchController.prototype.closeWhenNavigatingAway = function () {
    var queryParams = this.deps.UrlQueryStringProvider.getQueryParams(this.deps.Location.search || '?');
    var hasSearchActionInQueryParam = (!!queryParams.action && queryParams.action === 'search');

    if (!hasSearchActionInQueryParam && this.isSearchOverlayOpen) {
        window.WH.messageBus.publish(MessageBusTopics.SEARCH_CLOSE);
    }
};

/**
 * Initialize this component and setup if functionality is deemed as
 * enabled
 *
 * @returns {void}
 *
 * @public
 */
SearchController.prototype.init = function (deps) {

    this.deps = deps || {};
    this.deps.URLProvider = URLProvider;

    this.config = _.extend({}, window.WH.sportsbook.frontEndConfig.search, window.whStatic.config.search);
    this.i18n = window.WH.i18n;
    this.lang = window.WH.sportsbook.lang;
    const enabledForPlatform = (this.lang === 'es-es' || this.lang === 'it-it') ? true :
        this.isSearchEnabledForPlatform();

    if (this.isSearchEnabled() && enabledForPlatform) {
        window.WH.messageBus.subscribe(
            MessageBusTopics.DETECTION_MARKUP_INIT_COMPLETE, this.setup.bind(this)
        );
        window.WH.messageBus.subscribe(
            MessageBusTopics.NAVIGATION_BEFORE_PAGE_CHANGE, this.closeWhenNavigatingAway.bind(this)
        );
    }
};

/**
 * Create a Singleton()
 */
export default new SearchController();
